import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import '@/App.css';

import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';

import HomePage from '@/pages/HomePage';
import WhyValantiLabs from '@/pages/WhyValantiLabs';
import HowItWorks from '@/pages/HowItWorks';
import ForIndividuals from '@/pages/ForIndividuals';
import ForProfessionals from '@/pages/ForProfessionals';
import Technology from '@/pages/Technology';
import { ResearchList, ResearchDetail } from '@/pages/Research';
import Partners from '@/pages/Partners';
import EarlyAccess from '@/pages/EarlyAccess';
import Support from '@/pages/Support';
import Contact from '@/pages/Contact';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import AccountDeletion from '@/pages/AccountDeletion';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';

// Set REACT_APP_ENABLE_ADMIN=true at build time to include /admin and /admin/login.
// Production marketing builds leave it false so the admin surface does not ship.
const ADMIN_ENABLED = String(process.env.REACT_APP_ENABLE_ADMIN || '').toLowerCase() === 'true';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navigation />
      {children}
      <Footer />
    </>
  );
}

function NotFound() {
  return (
    <div className="vl-container py-32 text-center">
      <p className="mono-label text-vl-violet">404</p>
      <h1 className="font-serif-display text-4xl text-vl-ink mt-3">Page not found.</h1>
    </div>
  );
}

function useSmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true, lerp: 0.1 });
    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);
}

function MarketingApp() {
  useSmoothScroll();
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/why-valanti-labs" element={<PublicLayout><WhyValantiLabs /></PublicLayout>} />
      <Route path="/how-it-works" element={<PublicLayout><HowItWorks /></PublicLayout>} />
      <Route path="/for-individuals" element={<PublicLayout><ForIndividuals /></PublicLayout>} />
      <Route path="/for-professionals" element={<PublicLayout><ForProfessionals /></PublicLayout>} />
      <Route path="/technology" element={<PublicLayout><Technology /></PublicLayout>} />
      <Route path="/research" element={<PublicLayout><ResearchList /></PublicLayout>} />
      <Route path="/research/:slug" element={<PublicLayout><ResearchDetail /></PublicLayout>} />
      <Route path="/partners" element={<PublicLayout><Partners /></PublicLayout>} />
      <Route path="/early-access" element={<PublicLayout><EarlyAccess /></PublicLayout>} />
      <Route path="/support" element={<PublicLayout><Support /></PublicLayout>} />
      <Route path="/support/delete-account" element={<PublicLayout><AccountDeletion /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />

      <Route path="/admin/login" element={ADMIN_ENABLED ? <AdminLogin /> : <PublicLayout><NotFound /></PublicLayout>} />
      <Route path="/admin" element={ADMIN_ENABLED ? <AdminDashboard /> : <PublicLayout><NotFound /></PublicLayout>} />

      <Route path="*" element={
        <PublicLayout>
          <NotFound />
        </PublicLayout>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <MarketingApp />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}
